/**
 * ScoreCardsRow — handoff §3.4. Five cards across:
 *   1. Quality      (current / max points, weight 30%)
 *   2. Improvement Activities (current / max points, weight 15%)
 *   3. Promoting Interoperability (current / max points, weight 25%)
 *   4. Cost — always null in V1, renders "—" + caption pointing to available_after
 *   5. Projected total — visually emphasized
 *
 * Each card shows current / max, weight, and a goal-progress bar against
 * `goal_total`. IA + PI cards have an Edit button → modal that PUTs the
 * whole attestation list (replace-all semantics).
 *
 * Note on KpiCard: the stock `<KpiCard>` from `@tensaw/visualization` is
 * shaped for money/percent/integer values with a directional delta. Our
 * scores are "current / max" with a goal bar — too custom for KpiCard
 * (would require formatting both numerator and denominator and overlaying
 * a custom progress bar). Per the §10.1 fallback policy, we use a
 * Tensaw `<Card>` with composed children. Flag for v1.4: add a `GoalBar`
 * subcomponent to `@tensaw/visualization/kpi`.
 */
import { useState } from "react";
import { Card } from "@tensaw/design-system";
import type { ScoreBonus, ScoreCards, ScoreCardSection } from "@/api/models";
import { formatDate, formatPoints, toKpiNumber } from "@/lib/format";
import { IaEditModal } from "./IaEditModal";
import { PiEditModal } from "./PiEditModal";

import { type PinnedContext, pinnedTooltip } from "./types";

export interface ScoreCardsRowProps {
  clinicId: number;
  year: number;
  scoreCards: ScoreCards;
  pinned: PinnedContext | null;
}

/**
 * Compute the percentage fill of the goal bar.
 *
 * - For the four category cards: ratio of current/max → 0..100
 * - For the projected total: ratio of current / goal_total → 0..100
 * - Clamped to [0, 100] in either case.
 */
function pct(currentS: string | null | undefined, denominatorS: string | null | undefined): number {
  const cur = toKpiNumber(currentS);
  const den = toKpiNumber(denominatorS);
  if (cur === null || den === null || den === 0) return 0;
  return Math.min(100, Math.max(0, (cur / den) * 100));
}

interface ScoreCardChromeProps {
  label: string;
  weight: string | null | undefined; // "0.30" etc.
  currentS: string | null | undefined; // points
  maxS: string | null | undefined;
  caption?: React.ReactNode;
  emptyLabel?: string; // shown when current is null
  highlight?: boolean;
  editButton?: React.ReactNode;
  /** Optional goal bar fill percent (0-100). When undefined, no bar is shown. */
  fillPct?: number;
  /**
   * Optional secondary "raw" display line shown below the headline value.
   * Per V1.1 plan Q1 option (iii): weighted is headline (big), raw is
   * small grey secondary. e.g. "9 / 10 raw".
   */
  rawDisplay?: string;
  /**
   * V1.2.0 (backend v0.4.0): itemized bonus decomposition rendered as
   * inline chips below the raw line. Each chip shows the bonus amount and
   * kind (HP, small practice). The "undetermined" small-practice case
   * renders as "small-practice bonus: not determined" in italic muted
   * text — NEVER as "0" and NEVER as "not a small practice."
   *
   * Only the Quality card passes non-empty bonuses today; IA/PI/Cost
   * pass empty arrays.
   */
  bonuses?: ScoreBonus[];
}

function formatWeightPct(weight: string | null | undefined): string {
  const n = toKpiNumber(weight);
  if (n === null) return "";
  // Weight comes as a fraction ("0.30") OR sometimes "30" depending on
  // backend serialization. If <= 1, treat as fraction; otherwise as percent.
  const asPct = n <= 1 ? n * 100 : n;
  return `${Math.round(asPct)}%`;
}

/**
 * Human-friendly label for each bonus kind. Unknown kinds fall through to
 * the raw string so a new backend bonus type doesn't break the UI.
 */
function bonusKindLabel(kind: string): string {
  switch (kind) {
    case "small_practice":
      return "small practice";
    case "high_priority":
      return "HP";
    default:
      return kind;
  }
}

/**
 * Render the bonus decomposition as inline chips below the raw line.
 *
 * Per V1.2.0 plan Q1 (locked decision: inline chip, always present):
 *   - Determined non-zero amount → "+6 small practice" chip in muted tone
 *   - Determined zero amount → render nothing (the "not a small practice"
 *     case — absence conveys it cleanly)
 *   - Undetermined → "small-practice bonus: not determined" in italic
 *     muted text (NEVER "0", NEVER "not a small practice")
 *
 * Returns null if no bonuses to render so the parent doesn't add empty
 * whitespace.
 */
function BonusChips({ bonuses }: { bonuses: ScoreBonus[] }) {
  // Filter out determined-zero bonuses (they're noise). Keep determined
  // non-zero and ALL undetermined entries.
  const rendered = bonuses.filter((b) => {
    if (b.undetermined) return true;
    const n = toKpiNumber(b.amount);
    return n !== null && n !== 0;
  });
  if (rendered.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1 text-xs">
      {rendered.map((b, idx) => {
        const label = bonusKindLabel(b.kind);
        if (b.undetermined) {
          return (
            <span
              key={`${b.kind}-${idx}`}
              className="italic text-gray-500"
              title="The clinic's eligible-clinician count isn't set, so the small-practice bonus can't be determined yet."
            >
              {label} bonus: not determined
            </span>
          );
        }
        return (
          <span
            key={`${b.kind}-${idx}`}
            className="rounded bg-tw-teal-50 px-1.5 py-0.5 font-medium text-tw-teal-700 tnum"
            title={`+${formatPoints(b.amount)} ${label} bonus`}
          >
            +{formatPoints(b.amount)} {label}
          </span>
        );
      })}
    </div>
  );
}

function ScoreCardChrome({
  label,
  weight,
  currentS,
  maxS,
  caption,
  emptyLabel,
  highlight,
  editButton,
  fillPct,
  rawDisplay,
  bonuses,
}: ScoreCardChromeProps) {
  const isEmpty = currentS === null || currentS === undefined;
  const valueDisplay = isEmpty ? (emptyLabel ?? "—") : formatPoints(currentS);
  const max = formatPoints(maxS);

  return (
    <Card
      className={[
        "flex flex-col p-4",
        highlight ? "border-2 border-brand-primary" : "",
      ].join(" ")}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-600">{label}</span>
        <div className="flex items-center gap-2">
          {weight ? (
            <span className="text-xs font-medium text-gray-400">{formatWeightPct(weight)}</span>
          ) : null}
          {editButton}
        </div>
      </div>
      <div className={["mt-2 tnum leading-none", highlight ? "text-3xl font-bold" : "text-2xl font-semibold"].join(" ")}>
        <span className={isEmpty ? "text-gray-400" : "text-gray-800"}>{valueDisplay}</span>
        <span className="text-sm font-medium text-gray-400">/{max}</span>
      </div>
      {rawDisplay ? (
        <div className="mt-0.5 text-xs text-gray-400 tnum">{rawDisplay}</div>
      ) : null}
      {bonuses && bonuses.length > 0 ? <BonusChips bonuses={bonuses} /> : null}
      {caption ? (
        <div className="mt-2 text-xs text-gray-500">{caption}</div>
      ) : null}
      {fillPct !== undefined ? (
        <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100">
          <div
            className={highlight ? "h-full rounded-full bg-brand-primary" : "h-full rounded-full bg-tw-teal-500"}
            style={{ width: `${fillPct}%` }}
          />
        </div>
      ) : null}
    </Card>
  );
}

function editIconButton(
  onClick: () => void,
  label: string,
  disabled?: boolean,
  disabledTitle?: string,
) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-tw-teal-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
      title={disabled ? disabledTitle : label}
    >
      {/* Inline pencil glyph rather than lucide-react to keep deps minimal */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );
}

export function ScoreCardsRow({ clinicId, year, scoreCards, pinned }: ScoreCardsRowProps) {
  const [iaOpen, setIaOpen] = useState(false);
  const [piOpen, setPiOpen] = useState(false);

  const quality = scoreCards.quality;
  const ia = scoreCards.improvement_activities;
  const pi = scoreCards.promoting_interop;
  const cost: ScoreCardSection = scoreCards.cost;

  const costCaption = cost.available_after
    ? `CMS-computed · available ${formatDate(cost.available_after)}`
    : "CMS-computed";

  // Build the secondary "raw" line for each weighted card. Per the backend
  // v0.3.1 contract, ScoreCardSection.current/max are raw values; the
  // headline now reads from weighted_contribution/weighted_max instead.
  const rawLine = (s: ScoreCardSection): string | undefined => {
    if (s.current === null || s.current === undefined) return undefined;
    return `${formatPoints(s.current)} / ${formatPoints(s.max)} raw`;
  };

  const pinnedTip = pinned ? pinnedTooltip(pinned) : undefined;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <ScoreCardChrome
          label="Quality"
          weight={quality.weight}
          currentS={quality.weighted_contribution}
          maxS={quality.weighted_max}
          rawDisplay={rawLine(quality)}
          bonuses={quality.bonuses}
          fillPct={pct(quality.weighted_contribution, quality.weighted_max)}
        />
        <ScoreCardChrome
          label="Improvement Activities"
          weight={ia.weight}
          currentS={ia.weighted_contribution}
          maxS={ia.weighted_max}
          rawDisplay={rawLine(ia)}
          fillPct={pct(ia.weighted_contribution, ia.weighted_max)}
          editButton={editIconButton(
            () => setIaOpen(true),
            "Edit improvement activities",
            pinned !== null,
            pinnedTip,
          )}
        />
        <ScoreCardChrome
          label="Promoting Interop."
          weight={pi.weight}
          currentS={pi.weighted_contribution}
          maxS={pi.weighted_max}
          rawDisplay={rawLine(pi)}
          fillPct={pct(pi.weighted_contribution, pi.weighted_max)}
          editButton={editIconButton(
            () => setPiOpen(true),
            "Edit promoting interoperability",
            pinned !== null,
            pinnedTip,
          )}
        />
        <ScoreCardChrome
          label="Cost"
          weight={cost.weight}
          currentS={cost.weighted_contribution}
          maxS={cost.weighted_max}
          caption={costCaption}
          rawDisplay={rawLine(cost)}
          fillPct={0}
        />
        <ScoreCardChrome
          label="Projected total"
          weight={null}
          currentS={scoreCards.projected_total}
          maxS="100"
          highlight
          caption={
            <>
              Goal {formatPoints(scoreCards.goal_total)}
              {scoreCards.small_practice_bonus > 0 ? (
                <span> · +{scoreCards.small_practice_bonus} SP bonus</span>
              ) : null}
            </>
          }
          fillPct={pct(scoreCards.projected_total, scoreCards.goal_total ?? "100")}
        />
      </div>

      {iaOpen ? (
        <IaEditModal clinicId={clinicId} year={year} onClose={() => setIaOpen(false)} />
      ) : null}
      {piOpen ? (
        <PiEditModal clinicId={clinicId} year={year} onClose={() => setPiOpen(false)} />
      ) : null}
    </>
  );
}
